/**
 * SLA / work-leakage queue section (TRAIN-D-V4-P0-SLA-LEAKAGE-ENGINE-V1).
 *
 * TWO PARTS (mirrors ManagerTrackingSection):
 *  - SlaLeakageSection: the presentational section over a SlaLeakageResult prop
 *    (render tests drive it directly). Summary strip -> filtered list -> the
 *    ONE LeadQuickView drawer (STITCH-2). Holds only LOCAL UI state (selected
 *    class, open item).
 *  - SlaLeakagePanel (default export): the self-fetching, fault-isolated,
 *    FLAG-GATED container mounted on AgmDashboardPage. Renders NOTHING (dark)
 *    unless me.slaLeakageEnabled is present; else loading / error+Retry / empty
 *    / data.
 *
 * ACT (STITCH-2, the V4-P1 increment): the drawer is now the shared
 * role-aware LeadQuickView -- the assignment strip (claim / assign / release)
 * renders ONLY from the server-declared /me assignment fragment; a caller
 * with no live action still gets the honest read-only footer (SEE-NOT-DO
 * preserved for them). Content stays the PHI-safe leakage item.
 *
 * PLAIN LANGUAGE (package UI (b)): every class shows a human title + one-line
 * action hint (classMeta); NO snake_case anywhere in the UI. Severity colour is
 * ALWAYS reinforced by an icon + count + text (never colour alone; WCAG 1.4.1).
 *
 * PHI: ids / carved name / labels / timestamps only -- the payload carries no
 * clinical field (enforced backend-side; the drawer shows event LABELS +
 * timestamps, never free-text notes).
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  AlertTriangle,
  CalendarClock,
  CalendarPlus,
  ChevronRight,
  Clock,
  Moon,
  PauseCircle,
  UserPlus,
} from 'lucide-react';
import { fetchSlaLeakage } from '@/dashboard/api';
import { useDashboardMeContext } from '@/dashboard/me-context';
import type { DashboardMe } from '@/dashboard/types';
import LeadQuickView from '@/dashboard/leads/LeadQuickView';
import { buildQuickViewFromLeakageItem } from '@/dashboard/leads/lead-quickview-view';
import {
  SLA_CLASS_ORDER,
  assigneeLabel,
  APPOINTMENT_DATE_PASSED_HINT,
  classMeta,
  classScopeNote,
  displayNameOf,
  formatLeakageAge,
  isUnassigned,
  itemsForClass,
  summaryFor,
  totalBreachCount,
  type SlaBreachClass,
  type SlaBreachItemView,
  type SlaLeakageResult,
  type SlaSeverity,
} from '@/dashboard/sla-leakage/sla-leakage-view';

type IconType = typeof AlertTriangle;

/** One lucide icon per class = category identity (colour never encodes alone). */
const CLASS_ICON: Record<SlaBreachClass, IconType> = {
  unclaimed_lead_overdue: UserPlus,
  qualified_no_appointment: CalendarPlus,
  booked_not_confirmed: CalendarClock,
  stuck_in_stage: PauseCircle,
  no_activity: Moon,
};

/** The severity companion icon: red = alert, amber = clock, neutral = none. */
function SeverityIcon({ severity }: { severity: SlaSeverity }): JSX.Element | null {
  if (severity === 'red') {
    return <AlertTriangle className="sla-sevicon" aria-hidden="true" size={13} />;
  }
  if (severity === 'amber') {
    return <Clock className="sla-sevicon" aria-hidden="true" size={13} />;
  }
  return null;
}

/** A short severity word so the colour is never the only signal. */
function severityWord(severity: SlaSeverity): string {
  if (severity === 'red') return 'Critical';
  if (severity === 'amber') return 'Overdue';
  return 'Clear';
}

/** One summary KPI tile (package UI (a)): count + severity colour; click filters. */
function SummaryTile({
  breachClass,
  count,
  severity,
  active,
  scopeNote,
  onSelect,
}: {
  breachClass: SlaBreachClass;
  count: number;
  severity: SlaSeverity;
  active: boolean;
  /** Rider (b): the predicate-derived scope note (tooltip). */
  scopeNote: string;
  onSelect: (key: SlaBreachClass) => void;
}): JSX.Element {
  const meta = classMeta(breachClass);
  const Icon = CLASS_ICON[breachClass];
  return (
    <button
      type="button"
      className={`sla-tile sla-tile--${severity}${active ? ' sla-tile--active' : ''}`}
      data-testid={`sla-tile-${breachClass}`}
      data-severity={severity}
      aria-pressed={active}
      title={scopeNote}
      onClick={() => onSelect(breachClass)}
    >
      <span className="sla-tile__head">
        <Icon className="sla-tile__icon" aria-hidden="true" size={15} />
        <span className="sla-tile__title">{meta.title}</span>
      </span>
      <span className="sla-tile__count" data-testid={`sla-tile-count-${breachClass}`}>
        {count}
      </span>
      <span className="sla-tile__foot">
        <SeverityIcon severity={severity} />
        {count === 0 ? meta.hint : `${severityWord(severity)} - ${meta.hint}`}
      </span>
    </button>
  );
}

/** One leakage row (package UI (c)). Click opens the detail drawer. */
function LeakageRow({
  item,
  onOpen,
}: {
  item: SlaBreachItemView;
  onOpen: (item: SlaBreachItemView) => void;
}): JSX.Element {
  const unassigned = isUnassigned(item);
  return (
    <button
      type="button"
      className={`sla-row sla-row--${item.severity}`}
      data-testid="sla-row"
      data-severity={item.severity}
      onClick={() => onOpen(item)}
    >
      <span className="sla-row__main">
        <span className="sla-row__name">{displayNameOf(item)}</span>
        <span className="sla-row__meta">
          <span className="sla-row__clinic">{item.clinicLabel ?? 'Unknown clinic'}</span>
          {item.stageLabel !== null ? (
            <span className="sla-row__stage">{item.stageLabel}</span>
          ) : null}
          {unassigned ? (
            <span className="sla-row__badge" data-testid="sla-row-unassigned">
              Unassigned
            </span>
          ) : (
            <span className="sla-row__owner">{assigneeLabel(item)}</span>
          )}
        </span>
        {item.appointmentDatePassed === true ? (
          <span className="sla-row__hint" data-testid="sla-row-date-passed">
            {APPOINTMENT_DATE_PASSED_HINT}
          </span>
        ) : null}
      </span>
      <span className="sla-row__right">
        <span className={`sla-row__age sla-age--${item.severity}`}>
          <SeverityIcon severity={item.severity} />
          {formatLeakageAge(item.ageMinutes)}
        </span>
        <ChevronRight className="sla-row__chev" aria-hidden="true" size={16} />
      </span>
    </button>
  );
}

/** PURE presentational section over a resolved SlaLeakageResult. */
export function SlaLeakageSection({
  data,
  me = null,
  onChanged,
}: {
  data: SlaLeakageResult;
  /** The caller's /me payload -- drives the drawer's server-declared strip. */
  me?: DashboardMe | null;
  /** Refetch after a successful owner change in the drawer. */
  onChanged?: () => void;
}): JSX.Element {
  // Default the filter to the first class that has items, else the first class.
  const defaultClass = useMemo<SlaBreachClass>(() => {
    const withItems = SLA_CLASS_ORDER.find(
      (c) => summaryFor(data, c).count > 0,
    );
    return withItems ?? SLA_CLASS_ORDER[0];
  }, [data]);
  const [selected, setSelected] = useState<SlaBreachClass>(defaultClass);
  const [openItem, setOpenItem] = useState<SlaBreachItemView | null>(null);

  const total = totalBreachCount(data);
  const rows = itemsForClass(data, selected);
  const selectedMeta = classMeta(selected);

  return (
    <section className="sla-leakage" data-testid="sla-leakage" aria-label="Work leakage">
      <header className="sla-leakage__head">
        <div>
          <span className="sla-leakage__eyebrow">SLA and escalation</span>
          <h2 className="sla-leakage__title">Where work is leaking</h2>
        </div>
        <div className="sla-leakage__glance">
          <span className="sla-leakage__glance-num" data-testid="sla-total">
            {total}
          </span>
          {/* Rider (a) + E8 (LEAD-APPOINTMENT-CRM-COHERENCE-V1 SCOPE 6): these
              are leakage SIGNALS (a lead may breach more than one class), not
              unique leads -- and the label is now distinct from the Queue-health
              "Clinic SLA breaches" glance so the two numbers stop colliding under
              one name. */}
          <span className="sla-leakage__glance-label">SLA leakage signals</span>
        </div>
      </header>

      {data.capped ? (
        <p className="sla-leakage__note" data-testid="sla-capped-note">
          Showing the most recent records only - narrow your scope to see everything.
        </p>
      ) : null}

      <div className="sla-strip" data-testid="sla-strip">
        {SLA_CLASS_ORDER.map((c) => {
          const s = summaryFor(data, c);
          return (
            <SummaryTile
              key={c}
              breachClass={c}
              count={s.count}
              severity={s.severity}
              active={c === selected}
              scopeNote={classScopeNote(c, data.thresholds[c] ?? null)}
              onSelect={setSelected}
            />
          );
        })}
      </div>

      {total === 0 ? (
        <div className="sla-clear" data-testid="sla-clear">
          <span className="sla-clear__mark" aria-hidden="true">
            &#10003;
          </span>
          <p className="sla-clear__line">No leakage right now.</p>
          <p className="sla-clear__sub">Every lead and appointment is on track.</p>
        </div>
      ) : (
        <div className="sla-list-block">
          <div className="sla-list-block__head">
            <span className="sla-list-block__title">{selectedMeta.title}</span>
            <span className="sla-list-block__hint">{selectedMeta.hint}</span>
          </div>
          {rows.length === 0 ? (
            <p className="sla-list__empty" data-testid="sla-list-empty">
              Nothing in this list right now.
            </p>
          ) : (
            <div className="sla-list" data-testid="sla-list">
              {rows.map((item) => (
                <LeakageRow
                  key={`${item.refKind}-${item.appointmentId ?? item.safeLeadId}-${item.breachClass}`}
                  item={item}
                  onOpen={setOpenItem}
                />
              ))}
            </div>
          )}
        </div>
      )}

      {openItem !== null ? (
        <LeadQuickView
          model={buildQuickViewFromLeakageItem(openItem)}
          me={me}
          onClose={() => setOpenItem(null)}
          onChanged={() => {
            setOpenItem(null);
            onChanged?.();
          }}
        />
      ) : null}
    </section>
  );
}

/** The self-fetching, fault-isolated, FLAG-GATED panel on AgmDashboardPage. */
export default function SlaLeakagePanel(): JSX.Element | null {
  const ctx = useDashboardMeContext();
  const enabled = ctx?.me?.slaLeakageEnabled === true;

  const [data, setData] = useState<SlaLeakageResult | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    void fetchSlaLeakage()
      .then((d) => {
        if (!cancelled) setData(d);
      })
      .catch((e: unknown) => {
        if (!cancelled) {
          setError(e instanceof Error ? e.message : String(e));
          setData(null);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!enabled) return;
    return load();
  }, [enabled, load]);

  // Flag off (or no /me yet): fully dark -- byte-identical dashboard.
  if (!enabled) return null;

  if (error) {
    return (
      <section className="sla-leakage sla-leakage--error" data-testid="sla-error">
        <p className="sla-leakage__errline">Could not load work leakage. {error}</p>
        <button type="button" className="ac-btn ac-btn--secondary" onClick={() => load()}>
          Retry
        </button>
      </section>
    );
  }

  if (!data) {
    return (
      <section
        className="sla-leakage sla-leakage--loading"
        data-testid="sla-loading"
        aria-busy={loading}
      >
        <span className="sla-leakage__eyebrow">SLA and escalation</span>
        <div className="sla-strip sla-strip--skel" aria-hidden="true">
          <span className="sla-tile sla-tile--skel" />
          <span className="sla-tile sla-tile--skel" />
          <span className="sla-tile sla-tile--skel" />
        </div>
        <p className="sla-leakage__loadline">Loading work leakage&hellip;</p>
      </section>
    );
  }

  return (
    <SlaLeakageSection data={data} me={ctx?.me ?? null} onChanged={() => load()} />
  );
}
